import React from 'react';
import PropTypes from 'prop-types';

function formatAmount(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '--';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function isPresent(val) {
  return val !== null && val !== undefined && val !== '';
}

// Helper: Parse date string as local midnight if in YYYY-MM-DD format, and return
// formatted string
const parseLocalDate = (dateStr) => {
  if (!dateStr) return '';
  let dateObj;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    dateObj = new Date(`${dateStr}T00:00:00`);
  } else {
    dateObj = new Date(dateStr);
  }
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

function getDueDate({ earlyPay, paymentDueDate }) {
  if (earlyPay?.earlyPayEligibilityIndicator === true && earlyPay?.paymentDueDate) {
    return parseLocalDate(earlyPay.paymentDueDate);
  }
  if (paymentDueDate) {
    return parseLocalDate(paymentDueDate);
  }
  return '--';
}

function getEarlyPayBalanceFields({
  statementPeriods, openingBalance, adjustedBalance, closingBalance,
}) {
  const fields = [];
  if (
    isPresent(statementPeriods?.closingDate)
        && isPresent(openingBalance?.amount)
        && isPresent(adjustedBalance?.amount)
        && openingBalance?.amount !== adjustedBalance?.amount
  ) {
    fields.push({ title: 'Last Statement Balance', value: formatAmount(openingBalance?.amount) });
  }
  fields.push({ title: 'Total Balance', value: formatAmount(closingBalance?.amount) });
  fields.push({ title: 'Remaining Statement Balance', value: formatAmount(adjustedBalance?.amount) });
  return fields;
}

function getEarlyPayDueFields({
  deferralEligibilityIndicator, paymentDueBalance, earlyPay, paymentDueDate,
}) {
  const fields = [];
  if (deferralEligibilityIndicator === true) {
    fields.push({
      title: 'A payment of at least',
      value: formatAmount(paymentDueBalance?.amount),
    });
    fields.push({
      title: 'must be received by',
      value: getDueDate({ earlyPay, paymentDueDate }),
    });
    fields.push({
      title: 'This is the initial payment you must make to defer payment on your remaining statement balance.',
      value: '',
    });
  } else {
    fields.push({
      title: 'Due Date',
      value: getDueDate({ earlyPay, paymentDueDate }),
    });
    fields.push({
      title: 'Minimum Payment Due',
      value: formatAmount(paymentDueBalance?.amount),
    });
  }
  return fields;
}

function getEarlyPayPastDueField(pastPaymentDueBalance) {
  if (pastPaymentDueBalance?.amount > 0) {
    return [{ title: 'Past Due Balance', value: formatAmount(pastPaymentDueBalance?.amount) }];
  }
  return [];
}

function getEarlyPayOnlyFields({
  earlyPayEligibility, earlyPay, iaCode, adjustedBalance,
}) {
  const fields = [];
  if (earlyPayEligibility) {
    if (isPresent(earlyPay.discountBalance?.amount)) {
      fields.push({ title: 'You are eligible for an early pay discount of', value: formatAmount(earlyPay.discountBalance?.amount) });
    }
    if (iaCode === 'P6D') {
      fields.push({
        title: 'if any portion of your remaining statement balance of',
        value: formatAmount(adjustedBalance?.amount),
      });
    } else {
      fields.push({
        title: 'if your remaining statement balance payment of',
        value: formatAmount(adjustedBalance?.amount),
      });
    }
    fields.push({
      title: 'is received by',
      value: getDueDate({ earlyPay, paymentDueDate: undefined }),
    });
  }
  return fields;
}

function getEarlyPaySuppFields({
  accountRoleType, suppLimit, suppExposure, accountStatus,
}) {
  if (
    accountRoleType === 'SUPP'
        && isPresent(suppLimit)
        && isPresent(suppExposure)
        && accountStatus !== 'Cancelled'
  ) {
    return [
      { title: 'Your Spending Limit is', value: formatAmount(suppLimit) },
      { title: 'You have spent', value: formatAmount(suppExposure) },
    ];
  }
  return [];
}

function getEarlyPayFields({
  metaData,
  realTimeBalance,
  globalLimits,
  statementPeriods,
}) {
  const balancesArr = Array.isArray(realTimeBalance?.balances) ? realTimeBalance.balances : [];
  const balance = balancesArr[0] || {};
  const {
    balanceSummary = {},
    earlyPay = {},
    paymentDueDate,
    deferralEligibilityIndicator,
  } = balance;
  const {
    openingBalance,
    closingBalance,
    adjustedBalance,
    paymentDueBalance,
    pastPaymentDueBalance,
  } = balanceSummary;
  const earlyPayEligibility = earlyPay.earlyPayEligibilityIndicator === true;
  const iaCode = metaData?.GBL_API_PMC_IACode;
  const suppLimit = globalLimits?.open_to_buy_limits?.supp_limit;
  const suppExposure = globalLimits?.open_to_buy_limits?.supp_exposure;
  const accountRoleType = metaData?.IDV_API_DemographicsLookUp_AccountRoleType;
  const accountStatus = metaData?.IDV_API_DemographicsLookUp_AccountStatus;

  return [
    ...getEarlyPayBalanceFields({
      statementPeriods, openingBalance, adjustedBalance, closingBalance,
    }),
    ...getEarlyPayDueFields({
      deferralEligibilityIndicator, paymentDueBalance, earlyPay, paymentDueDate,
    }),
    ...getEarlyPayPastDueField(pastPaymentDueBalance),
    ...getEarlyPayOnlyFields({
      earlyPayEligibility, earlyPay, iaCode, adjustedBalance,
    }),
    ...getEarlyPaySuppFields({
      accountRoleType, suppLimit, suppExposure, accountStatus,
    }),
  ];
}

function getCorporateOrEmeraldFields({
  metaData, balanceSummary, paymentDueDate, statementPeriods, globalLimits,
}) {
  const codes = [
    'CORPRT', 'LU7', 'LU8', 'LU9', 'LVA', 'LVB', 'LVD', 'LVK', 'LVR', 'LVX', 'LV5',
    'M8P', 'M8Q', 'M8R', 'M8S', 'M8T', 'M8U', 'M8V', 'M8W', 'M8X', 'M8Y',
    'QL5', 'QL6', 'QL7', 'QL8', 'QL9', 'QMA', 'QMB', 'QMC', 'QMD',
  ];
  const isCorporateOrEmerald = codes.includes(metaData?.GBL_API_PMC_HLPC)
        || codes.includes(metaData?.GBL_API_PMC_IACode);
  if (!isCorporateOrEmerald) return [];
  const pastDueAmount = Number.parseFloat(balanceSummary?.pastPaymentDueBalance?.amount);
  const fields = [];
  const closingDate = statementPeriods?.closingDate;
  const openingBalanceAmount = balanceSummary?.openingBalance?.amount;
  const remainingStatementBalanceAmount = balanceSummary?.remainingStatementBalance?.amount;
  if (
    closingDate
        && openingBalanceAmount != null && openingBalanceAmount !== ''
        && remainingStatementBalanceAmount != null && remainingStatementBalanceAmount !== ''
        && openingBalanceAmount !== remainingStatementBalanceAmount
  ) {
    fields.push({ title: 'Last Statement Balance', value: formatAmount(openingBalanceAmount) });
  }
  fields.push(
    { title: 'Total Balance', value: formatAmount(balanceSummary.closingBalance?.amount) },
    { title: 'Payment Due', value: formatAmount(balanceSummary.paymentDueBalance?.amount) }
  );
  if (pastDueAmount > 0) {
    fields.push({ title: 'Past Due Amount', value: formatAmount(pastDueAmount) });
  }
  fields.push({
    title: 'Due Date',
    value: paymentDueDate ? parseLocalDate(paymentDueDate) : '--',
  });
  // Add Supp fields if not blank/null
  const suppLimit = globalLimits?.open_to_buy_limits?.supp_limit;
  const suppExposure = globalLimits?.open_to_buy_limits?.supp_exposure;
  if (suppLimit !== null && suppLimit !== undefined && suppLimit !== '') {
    fields.push({ title: 'Your Spending Limit is', value: formatAmount(suppLimit) });
  }
  if (suppExposure !== null && suppExposure !== undefined && suppExposure !== '') {
    fields.push({ title: 'You have spent', value: formatAmount(suppExposure) });
  }
  return fields;
}

function getFlexFields(balanceSummary) {
  if (!(balanceSummary?.flexBalance?.openingBalance?.amount > 0)) return [];
  return [
    { title: 'Last Statement Balance', value: formatAmount(balanceSummary.openingBalance?.amount) },
    { title: 'Total Balance', value: formatAmount(balanceSummary.closingBalance?.amount) },
    { title: 'Pay Over Time Payment Balance', value: formatAmount(balanceSummary.flexBalance?.openingBalance?.amount) },
    { title: 'Minimum Payment', value: formatAmount(balanceSummary.paymentDueBalance?.amount) },
  ];
}

function getDefaultChargeCardFields({
  metaData, realTimeBalance, globalLimits, statementPeriods, planSummary,
}) {
  const balancesArr = Array.isArray(realTimeBalance?.balances) ? realTimeBalance.balances : [];
  const balanceSummary = balancesArr[0]?.balanceSummary || {};
  const paymentDueDate = balancesArr[0]?.paymentDueDate;
  const adjustedBalance = Number(balanceSummary.adjustedBalance?.amount);
  const openingBalance = Number(balanceSummary.openingBalance?.amount);
  const closingBalance = Number(balanceSummary.closingBalance?.amount);
  const paymentDueBalance = Number(balanceSummary.paymentDueBalance?.amount);
  const pastPaymentDueBalance = Number(balanceSummary.pastPaymentDueBalance?.amount);

  const fields = [];

  // Last Statement Balance
  const prevStatement = statementPeriods?.closingDate;
  if (
    prevStatement
        && openingBalance
        && adjustedBalance
        && openingBalance !== adjustedBalance
  ) {
    fields.push({ title: 'Last Statement Balance', value: formatAmount(openingBalance) });
  }

  // Total Balance
  fields.push({ title: 'Total Balance', value: formatAmount(closingBalance) });

  // Minimum Payment Due (if active plan and adjustedBalance > 0)
  if (adjustedBalance > 0 && planSummary?.activePlansCount > 0) {
    fields.push({ title: 'Minimum Payment Due', value: formatAmount(paymentDueBalance) });
    fields.push({ title: 'Adjusted Balance', value: formatAmount(adjustedBalance) });
  } else {
    // Payment Due
    fields.push({ title: 'Payment Due', value: formatAmount(paymentDueBalance) });
  }

  // Past Due Amount
  if (pastPaymentDueBalance > 0) {
    fields.push({ title: 'Past Due Amount', value: formatAmount(pastPaymentDueBalance) });
  }

  // Due Date
  fields.push({ title: 'Due Date', value: paymentDueDate ? parseLocalDate(paymentDueDate) : '--' });

  // Supp fields
  if (
    metaData?.IDV_API_DemographicsLookUp_AccountRoleType === 'SUPP'
        && globalLimits?.open_to_buy_limits?.supp_limit != null
        && globalLimits?.open_to_buy_limits?.supp_exposure != null
        && metaData?.IDV_API_DemographicsLookUp_AccountStatus !== 'Cancelled'
  ) {
    fields.push({ title: 'Your Spending Limit is', value: formatAmount(globalLimits.open_to_buy_limits.supp_limit) });
    fields.push({ title: 'You have spent', value: formatAmount(globalLimits.open_to_buy_limits.supp_exposure) });
  }
  return fields;
}

function getChargeCardFields({
  metaData, realTimeBalance, globalLimits, statementPeriods, planSummary,
}) {
  const balancesArr = Array.isArray(realTimeBalance?.balances) ? realTimeBalance.balances : [];
  const balanceSummary = balancesArr[0]?.balanceSummary || {};
  // Flex - done
  if (balanceSummary?.flexBalance?.openingBalance?.amount > 0) {
    return getFlexFields(balanceSummary);
  }
  // Early Pay by IA Code
  const earlyPayIACodes = ['P6D', 'P6E', 'P6F', 'P6G', 'P6H', 'P6J'];
  if (earlyPayIACodes.includes(metaData?.GBL_API_PMC_IACode)) {
    return getEarlyPayFields({
      metaData,
      realTimeBalance,
      globalLimits,
      statementPeriods,
    });
  }
  // Corp/One
  const codes = [
    'CORPRT', 'LU7', 'LU8', 'LU9', 'LVA', 'LVB', 'LVD', 'LVK', 'LVR', 'LVX', 'LV5',
    'M8P', 'M8Q', 'M8R', 'M8S', 'M8T', 'M8U', 'M8V', 'M8W', 'M8X', 'M8Y',
    'QL5', 'QL6', 'QL7', 'QL8', 'QL9', 'QMA', 'QMB', 'QMC', 'QMD',
  ];
  const isCorporateOrEmerald = codes.includes(metaData?.GBL_API_PMC_HLPC)
        || codes.includes(metaData?.GBL_API_PMC_IACode);
  if (isCorporateOrEmerald) {
    return getCorporateOrEmeraldFields({
      metaData,
      balanceSummary,
      paymentDueDate: balancesArr[0]?.paymentDueDate,
      statementPeriods,
      globalLimits,
    });
  }
  // Default
  return getDefaultChargeCardFields({
    metaData, realTimeBalance, globalLimits, statementPeriods, planSummary,
  });
}

const ChargeCardSummary = ({
  metaData, realTimeBalance, globalLimits, paymentHistory, statementPeriods, planSummary,
}) => {
  const fields = getChargeCardFields({
    metaData, realTimeBalance, globalLimits, paymentHistory, statementPeriods, planSummary,
  });

  const visibleFields = fields.filter((field) => field.value !== '--');

  return (
    <div className="width-full">
      {visibleFields.map((field) => (
        <div key={field.title} className="flex flex-justify-between width-full">
          <p className="body-3 text-align-left dls-gray-05">{field.title}</p>
          <p className="body-3">{field.value}</p>
        </div>
      ))}
    </div>
  );
};

ChargeCardSummary.propTypes = {
  metaData: PropTypes.shape({
    GBL_API_PMC_HLPC: PropTypes.string,
    GBL_API_PMC_IACode: PropTypes.string,
    GBL_API_PMC_paymentType: PropTypes.string,
    IDV_API_DemographicsLookUp_AccountRoleType: PropTypes.string,
    IDV_API_DemographicsLookUp_AccountStatus: PropTypes.string,
  }),
  realTimeBalance: PropTypes.shape({
    balances: PropTypes.arrayOf(PropTypes.shape({
      earlyPay: PropTypes.shape({
        earlyPayEligibilityIndicator: PropTypes.bool,
        discountBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        paymentDueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      balanceSummary: PropTypes.shape({
        openingBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        closingBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        adjustedBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        paymentDueBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        pastPaymentDueBalance: PropTypes.shape({
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        flexBalance: PropTypes.shape({
          openingBalance: PropTypes.shape({
            amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          }),
        }),
      }),
      paymentDueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }),
  globalLimits: PropTypes.shape({
    open_to_buy_limits: PropTypes.shape({
      supp_limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      supp_exposure: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  paymentHistory: PropTypes.shape({
    remittances: PropTypes.arrayOf(PropTypes.shape({
      status: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      paymentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }),
  statementPeriods: PropTypes.shape({
    billingPeriods: PropTypes.arrayOf(PropTypes.shape({
      index: PropTypes.number,
      closingDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }),
  planSummary: PropTypes.shape({
    activePlansCount: PropTypes.number,
  }),
};

export default ChargeCardSummary;
